import { TRYON_SERVER_URL } from "../config";

const TRYON_MAX_PIECES = 4;

/** One piece: 60 at 2K, 75 at 4K, 45 at 1K. Two to four pieces: 120 at 2K. */
const tryonCredits = (pieceCount, resolution = "2k") => {
    const count = Number(pieceCount) || 0;
    if (count >= 2) return 120;
    const size = String(resolution || "2k").toLowerCase();
    if (size === "4k") return 75;
    if (size === "1k") return 45;
    return 60;
};

/**
 * Start a virtual try-on. The server picks the provider.
 * Person and garments are already uploaded experiment images.
 *
 * @param {Object} options
 * @param {string} options.person_id
 * @param {string} [options.garment_id]
 * @param {string[]} [options.garment_ids]
 * @param {string} [options.resolution]
 * @param {Object} [options.params]
 * @param {string} [options.accessToken]
 * @returns {Promise<Object>}
 */
const generateVirtualTryOn = async ({
    person_id,
    garment_id,
    garment_ids,
    resolution = "2k",
    params = {},
    accessToken = null,
}) => {
    try {
        const ids = (Array.isArray(garment_ids) && garment_ids.length ? garment_ids : [garment_id]).filter(Boolean);
        if (!person_id || ids.length === 0) {
            return { data: null, status: "error", statusCode: 400, message: "Choose a person and a garment" };
        }

        const endpoint = `${TRYON_SERVER_URL}/api/v1/vton/generate/`;
        const formData = new FormData();
        formData.append("person_id", person_id);
        if (ids.length === 1) {
            formData.append("garment_id", ids[0]);
            formData.append("resolution", resolution || "2k");
        } else {
            ids.slice(0, TRYON_MAX_PIECES).forEach((id) => formData.append("garment_ids", id));
            formData.append("resolution", "2k");
        }

        if (params && Object.keys(params).length) {
            formData.append("params", JSON.stringify(params));
        }

        const headers = {};
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetch(endpoint, {
            method: "POST",
            headers,
            body: formData,
        });

        const payload = await response.json().catch(() => null);

        if (response.ok) {
            return {
                data: payload,
                status: "success",
                statusCode: response.status,
                message: "Virtual try-on generated successfully",
            };
        }

        const errorMessage = payload?.message || payload?.error || "Failed to trigger virtual try-on";
        return {
            data: null,
            status: "failure",
            statusCode: response.status,
            message: errorMessage,
        };
    } catch (error) {
        console.error("Error generating virtual try-on:", error);
        return {
            data: null,
            status: "failure",
            statusCode: 500,
            message: "Network error: " + error.message,
        };
    }
};

const FLAT_LAY_CREDITS = 60;
const FACE_REFERENCE_CREDITS = 45;

const flatLayCredits = (hasFace) =>
    FLAT_LAY_CREDITS + (hasFace ? FACE_REFERENCE_CREDITS : 0);

/**
 * Place a garment photo on a model. The server picks the provider.
 * A face photo is optional and adds to the price.
 */
const productOnModel = async ({
    garmentId,
    prompt = "",
    faceFile = null,
    accessToken = null,
}) => {
    try {
        if (!garmentId) {
            return { data: null, status: "error", statusCode: 400, message: "Choose a garment" };
        }

        const formData = new FormData();
        formData.append("garment_id", garmentId);
        if (String(prompt || "").trim()) {
            formData.append("prompt", String(prompt).trim());
        }
        if (faceFile) {
            formData.append("face_reference", faceFile);
        }

        const headers = {};
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetch(`${TRYON_SERVER_URL}/api/v1/product/on-model/`, {
            method: "POST",
            headers,
            body: formData,
        });
        const payload = await response.json().catch(() => null);

        if (response.ok && payload?.result?.image_url) {
            return {
                data: payload,
                status: "success",
                statusCode: response.status,
                message: "The garment is on a model",
            };
        }

        return {
            data: null,
            status: "failure",
            statusCode: response.status,
            message: payload?.message || payload?.error || "Could not place the garment on a model",
        };
    } catch (error) {
        console.error("Error placing garment on a model:", error);
        return {
            data: null,
            status: "failure",
            statusCode: 500,
            message: "Network error: " + error.message,
        };
    }
};

export { generateVirtualTryOn, tryonCredits, TRYON_MAX_PIECES, flatLayCredits, productOnModel };
